import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Backdrop, Grow, Modal } from '@material-ui/core';
import * as Utils from '../../commons/utils/Utils.js';
import { ICONO_CONFIRMAR, ICONO_CANCELAR } from '../../commons/dialog/Dialog.js';

/**
 * VEC-3318 — cancelar lo que falta entregar de una línea pendiente.
 *
 * No toca existencias: lo ya despachado queda despachado y la diferencia se da de baja. Cancelar una
 * línea parcialmente entregada es legítimo, y por eso el modal muestra los dos números.
 *
 * EL MOTIVO ES OBLIGATORIO, y se valida en los dos lados a propósito: acá para no dejar apretar el
 * botón, y en el backend porque el endpoint también se consume fuera de esta pantalla. El backend
 * además rechaza una cadena de espacios, que es lo que `required` del HTML dejaría pasar.
 *
 * El motivo no se guarda en la solicitud sino en el movimiento de cancelación, junto con quién y
 * cuándo — por eso el histórico de gestiones lo muestra y el detalle no lo duplica.
 *
 * ESTRUCTURA BOOTSTRAP, NO `DialogTitle`/`DialogContent`/`DialogActions`.
 *
 * Esos tres traen sus PROPIOS paddings (`MuiDialogTitle-root`, `MuiDialogContent-root`,
 * `MuiDialogActions-root`) y se aplican ENCIMA de `modal-header` / `modal-body` / `modal-footer`,
 * así que el modal quedaba con márgenes distintos a los ~95 modales Bootstrap del sistema. Es lo que
 * advierte docs/design-system/dialogos-y-modales.md ("Evitar Dialog / Modal de Material-UI para
 * modales nuevos") y lo mismo que hizo descartar la primera versión de GridHistoryModal.
 *
 * `Modal` de MUI queda SOLO como contenedor —backdrop, portal y foco—, igual que en
 * TicketsGridExport. La caja la arma Bootstrap, y por eso el modal se alinea arriba y mide lo que
 * miden los demás.
 */

const BASE_URL = '/inventario/solicitudes/detalles';

/**
 * Tope de la observación. Es el `maxLength` del input Y el denominador del contador, así que el
 * número vive en UN solo lugar: mostrar "/ 255" y dejar escribir 300 es peor que no mostrar nada.
 * Coincide con el `varchar(255)` de `item_stock_movimientos.motivo`.
 */
const MAX_MOTIVO = 255;

const InventarioSolicitudCancelarModal = (props) => {
  const detalle = props.detalle;

  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);

  // Al abrir con otra línea se limpia: si quedara el motivo anterior, se podría cancelar una línea
  // con la justificación de otra.
  useEffect(() => {
    if (detalle) setMotivo('');
  }, [detalle && detalle.id]);

  const puedeGuardar = () => !guardando && motivo.trim() !== '';

  const handleGuardar = () => {
    if (!puedeGuardar()) return;

    setGuardando(true);
    Utils.sendData(BASE_URL + '/' + detalle.id + '/cancelar', 'POST', { motivo: motivo.trim() })
      .then((data) => props.onCancelado(data))
      // `Utils.sendData` ya muestra el error por Swal; acá solo se corta la propagación.
      .catch(() => {})
      .finally(() => setGuardando(false));
  };

  if (!detalle) return null;

  const item = detalle.item || {};
  const pendiente = Number(detalle.cantidadPendiente);
  const despachado = Number(detalle.cantidadDespachada);

  return (
    <Modal open={!!detalle} onClose={props.onClose} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 300 }}>
      <Grow in={!!detalle}>
        <div tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header bg-fleet">
                <h4 className="modal-title text-white mb-0">
                  {/* La misma manito que la acción de la fila: `fa-ban` se leía como "anular", que
                      es otra cosa. */}
                  <i className="fa fa-thumbs-down align-middle icon-modal-title"></i>
                  <FormattedMessage id="inventarioSolicitudes.cancelar.titulo" defaultMessage=" Rechazar solicitud" />
                </h4>
                {/* La × de cerrar: va en TODOS los modales del sistema y es la salida
                    que la gente busca primero. */}
                <button type="button" className="close" aria-label="Close" onClick={props.onClose}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div className="modal-body modal-grey modal-mh ovf-x-hidden">
                <div className="mb-3">
                  <div style={{ fontWeight: 600 }}>{item.numeroDeParte ? item.numeroDeParte + ' · ' : ''}{item.descripcion}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    <FormattedMessage
                      id="inventarioSolicitudes.cancelar.contexto"
                      defaultMessage="Ticket {ticket} · Depósito {deposito}"
                      values={{
                        ticket: detalle.ticket ? detalle.ticket.id : '-',
                        deposito: detalle.ubicacion ? detalle.ubicacion.nombre : '-',
                      }}
                    />
                  </div>
                </div>

                {/* Qué se da de baja exactamente. Con entregas parciales la diferencia entre lo pedido y lo
                    entregado no es evidente, y cancelar sin verla es fácil de hacer mal. */}
                <div className="alert alert-warning">
                  {despachado > 0 ? (
                    <FormattedMessage
                      id="inventarioSolicitudes.cancelar.aviso_parcial"
                      defaultMessage="Se dan de baja {pendiente} unidades. Las {despachado} ya entregadas no se modifican y el stock no se toca."
                      values={{ pendiente: <b>{pendiente}</b>, despachado: <b>{despachado}</b> }}
                    />
                  ) : (
                    <FormattedMessage
                      id="inventarioSolicitudes.cancelar.aviso_total"
                      defaultMessage="Se dan de baja {pendiente} unidades. El stock no se modifica."
                      values={{ pendiente: <b>{pendiente}</b> }}
                    />
                  )}
                </div>

                <div className="form-group mb-0">
                  <label htmlFor="cancelarMotivo">
                    <FormattedMessage id="inventarioSolicitudes.cancelar.motivo" defaultMessage="Motivo" />
                    <span className="text-danger"> *</span>
                  </label>
                  <textarea
                    id="cancelarMotivo"
                    className="form-control"
                    rows={3}
                    maxLength={MAX_MOTIVO}
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                  />
                  {/* Cuánto queda por escribir. `maxLength` ya frena en seco, pero sin contador el
                      usuario descubre el tope recién cuando el campo deja de responder —y ahí no
                      sabe si se rompió algo—. */}
                  <small className="text-muted float-right">
                    {motivo.length} / {MAX_MOTIVO}
                  </small>
                  <small className="text-muted">
                    <FormattedMessage
                      id="inventarioSolicitudes.cancelar.motivo_ayuda"
                      defaultMessage="Queda registrado en el historial del depósito, junto con quién rechazó y cuándo."
                    />
                  </small>
                </div>
              </div>

      {/* Mismo orden y mismos iconos que el resto (design system): confirmar a la izquierda.
          El PRIMARIO es el que confirma el rechazo, aunque la acción sea destructiva: el rojo está
          reservado para "salir sin hacer nada", que es la convención del sistema. */}
              <div className="modal-footer modal-grey">
                <button type="button" className="btn btn-primary" onClick={handleGuardar} disabled={!puedeGuardar()}>
                  <i className={ICONO_CONFIRMAR + ' mr-1'} />
                  <FormattedMessage id="inventarioSolicitudes.cancelar.confirmar" defaultMessage="Rechazar solicitud" />
                </button>
                <button type="button" className="btn btn-danger" onClick={props.onClose} disabled={guardando}>
                  <i className={ICONO_CANCELAR + ' mr-1'} />
                  <FormattedMessage id="inventarioSolicitudes.cancelar.volver" defaultMessage="Volver" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Grow>
    </Modal>
  );
};

export default InventarioSolicitudCancelarModal;
